import { Box, DialogActions, Paper, Typography } from "@mui/material";
import ModalBasicLayout from "./ModalBasicLayout";
import { Trans, useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import CancellationReasonForm from "../Form/CancellationReasonForm";
import TextField from "../Form/TextField";
import Button from "../Buttons/Button";
import {
  MODAL_CANCEL_SUBSCRIPTION_TITLE,
  MODAL_CANCEL_SUBSCRIPTION_SUBTITLE,
  MODAL_CANCEL_SUBSCRIPTION_SUBTITLE_WARNING,
  MODAL_CANCEL_SUBSCRIPTION_CONFIRM_BUTTON,
  MODAL_CANCEL_SUBSCRIPTION_BASIC_RETENTION_INTRO,
  MODAL_CANCEL_SUBSCRIPTION_BASIC_RETENTION_LIMITED_OFFER,
  MODAL_CANCEL_SUBSCRIPTION_BASIC_RETENTION_PRICING_FROM_DATE,
  MODAL_CANCEL_SUBSCRIPTION_BASIC_RETENTION_PRICING_NO_DATE,
  MODAL_CANCEL_SUBSCRIPTION_BASIC_RETENTION_IMMEDIATE_CREDITS,
  MODAL_CANCEL_SUBSCRIPTION_BASIC_RETENTION_TAKE_OFFER_BUTTON,
  BUTTON_GO_BACK,
  BUTTON_CANCEL_PLAN_AND_DELETE_CAMPAIGNS,
  BUTTON_KEEP_MY_ACCOUNT,
  MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_TITLE,
  MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_INTRO,
  MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_BULLET_CAMPAIGNS,
  MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_BULLET_GALLERY,
  MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_BULLET_DATA,
  MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_BULLET_API_CONNECTIONS,
  MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_RECOMMENDATION_INTRO,
  MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_RECOMMENDATION_BULLET_DOWNLOAD_GALLERY,
  MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_RECOMMENDATION_BULLET_EXPORT_REPORTS,
  MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_RECOMMENDATION_BULLET_DUPLICATE_CAMPAIGNS,
  MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_NO_RECOVERY,
  MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_CONFIRM_PROMPT,
  MODAL_CANCEL_SUBSCRIPTION_CONFIRM_WORD,
  CANCELLATION_REASON_TOO_EXPENSIVE,
  CANCELLATION_REASON_NOT_FREQUENT_USE,
  CANCELLATION_REASON_BETTER_ALTERNATIVE,
  CANCELLATION_REASON_TECHNICAL_ISSUES,
  CANCELLATION_REASON_NOT_MEET_NEEDS,
  CANCELLATION_REASON_DIFFICULT_TO_USE,
  CANCELLATION_REASON_SITUATION_CHANGED,
  CANCELLATION_REASON_TEMPORARY_NEED,
  CANCELLATION_REASON_SECURITY_CONCERNS,
  CANCELLATION_REASON_OTHER,
  MODAL_CANCEL_SUBSCRIPTION_RETENTION_OFFER_REDEEMED_SUCCESS,
} from "../../i18n/keysTranslations";
import ContainerModalActions from "../Containers/ContainerModalActions";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { startDowngradeSubscriptionForRetention } from "../../actions/payments";
import { getNameBusinessTheme } from "../../actions/getters";
import { getEntryPlanForVersion } from "../../utils/creditActionCosts";
import {
  ALERT_ICON_TYPE_SUCCESS,
  PLAN_TYPE_CREDITS,
} from "../../utils/constants";
import { BOX_SHADOW_SOLID } from "../../utils/colors";
import { SimpleAlert } from "../Alerts/Alerts";

/** Shared "callout card" look for retention offer and destructive-confirm copy */
const cancelFlowCalloutPaperSx = {
  width: "100%",
  maxWidth: 600,
  alignSelf: "center",
  py: 2.25,
  px: 2.5,
  borderRadius: 2,
  bgcolor: "background.paper",
  border: "1px solid",
  borderColor: "divider",
  boxShadow: BOX_SHADOW_SOLID,
};

const destructiveBulletSx = {
  m: 0,
  pl: 2.5,
  listStyleType: "disc",
  "& li": {
    mb: 0.75,
    lineHeight: 1.5,
  },
  "& li:last-child": {
    mb: 0,
  },
};

const cancelFlowStrongSx = { fontWeight: 700 };

const cancelFlowStrongDangerSx = {
  fontWeight: 700,
  color: "error.main",
};

const cancelFlowTransComponents = {
  bold: <Box component="strong" sx={cancelFlowStrongSx} />,
  boldDanger: <Box component="strong" sx={cancelFlowStrongDangerSx} />,
};

const CancelSubscriptionBasicRetentionCallout = ({
  renewDate,
  monthlyPrice,
  credits,
  onTakeOffer = () => {},
  loading = false,
}) => {
  const { t } = useTranslation();

  const pricingLine = renewDate
    ? t(MODAL_CANCEL_SUBSCRIPTION_BASIC_RETENTION_PRICING_FROM_DATE, {
        date: new Date(renewDate),
        price: monthlyPrice,
        credits,
      })
    : t(MODAL_CANCEL_SUBSCRIPTION_BASIC_RETENTION_PRICING_NO_DATE, {
        price: monthlyPrice,
        credits,
      });

  return (
    <Paper
      elevation={0}
      sx={{
        ...cancelFlowCalloutPaperSx,
        mt: 2,
        mb: 1,
        borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`,
      }}
    >
      <Typography
        variant="subtitle2"
        component="p"
        sx={{
          fontWeight: 700,
          color: "text.primary",
          mb: 1.25,
          lineHeight: 1.45,
        }}
      >
        {t(MODAL_CANCEL_SUBSCRIPTION_BASIC_RETENTION_LIMITED_OFFER)}
      </Typography>
      <Typography
        variant="body2"
        sx={{ mb: 1.25, color: "text.secondary", lineHeight: 1.5 }}
      >
        {t(MODAL_CANCEL_SUBSCRIPTION_BASIC_RETENTION_INTRO)}
      </Typography>
      <Typography
        variant="subtitle1"
        component="p"
        sx={{ fontWeight: 700, color: "text.primary", mb: 1, lineHeight: 1.45 }}
      >
        {pricingLine}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", lineHeight: 1.5, mb: 1.5 }}
      >
        {t(MODAL_CANCEL_SUBSCRIPTION_BASIC_RETENTION_IMMEDIATE_CREDITS)}
      </Typography>
      <Button
        color="secondary"
        size="small"
        onClick={onTakeOffer}
        loading={loading}
        sx={{
          alignSelf: "flex-start",
          minWidth: "auto",
          height: 36,
          py: 0.5,
          px: 1.5,
          fontSize: "13px !important",
        }}
      >
        {t(MODAL_CANCEL_SUBSCRIPTION_BASIC_RETENTION_TAKE_OFFER_BUTTON)}
      </Button>
    </Paper>
  );
};

const CancelSubscriptionDestructiveContent = ({
  NAME_BUSINESS,
  control,
  errors,
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ width: "100%", maxWidth: 600, alignSelf: "center" }}>
      <Typography
        variant="body1"
        component="div"
        sx={{ lineHeight: 1.5, mb: 1.5 }}
      >
        <Trans
          i18nKey={MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_INTRO}
          values={{ NAME_BUSINESS }}
          components={cancelFlowTransComponents}
        />
      </Typography>

      <Box component="ul" sx={{ ...destructiveBulletSx, mb: 1.5 }}>
        <Typography component="li" variant="body2">
          {t(MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_BULLET_CAMPAIGNS, {
            NAME_BUSINESS,
          })}
        </Typography>
        <Typography component="li" variant="body2">
          {t(MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_BULLET_GALLERY)}
        </Typography>
        <Typography component="li" variant="body2">
          {t(MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_BULLET_DATA)}
        </Typography>
        <Typography component="li" variant="body2">
          {t(MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_BULLET_API_CONNECTIONS)}
        </Typography>
      </Box>

      <Typography
        variant="body1"
        component="div"
        sx={{ lineHeight: 1.5, mb: 1.5 }}
      >
        <Trans
          i18nKey={MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_RECOMMENDATION_INTRO}
          components={cancelFlowTransComponents}
        />
      </Typography>

      <Box component="ul" sx={{ ...destructiveBulletSx, mb: 1.5 }}>
        <Typography component="li" variant="body2">
          {t(
            MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_RECOMMENDATION_BULLET_DOWNLOAD_GALLERY
          )}
        </Typography>
        <Typography component="li" variant="body2">
          {t(
            MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_RECOMMENDATION_BULLET_EXPORT_REPORTS
          )}
        </Typography>
        <Typography component="li" variant="body2">
          {t(
            MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_RECOMMENDATION_BULLET_DUPLICATE_CAMPAIGNS
          )}
        </Typography>
      </Box>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          color: "error.main",
          lineHeight: 1.5,
          mb: 2.5,
        }}
      >
        {t(MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_NO_RECOVERY, {
          NAME_BUSINESS,
        })}
      </Typography>

      <Typography
        variant="body2"
        component="div"
        sx={{ color: "text.primary", mb: 1 }}
      >
        <Trans
          i18nKey={MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_CONFIRM_PROMPT}
          components={cancelFlowTransComponents}
        />
      </Typography>
      <TextField
        control={control}
        name="destructiveConfirm"
        variant="filled"
        fullWidth
        rules={{}}
        errors={errors}
        autoComplete="off"
      />
    </Box>
  );
};

const cancellationReasons = [
  { value: "tooExpensive", label: CANCELLATION_REASON_TOO_EXPENSIVE },
  {
    value: "notFrequentUse",
    label: CANCELLATION_REASON_NOT_FREQUENT_USE,
  },
  {
    value: "betterAlternative",
    label: CANCELLATION_REASON_BETTER_ALTERNATIVE,
  },
  {
    value: "technicalIssues",
    label: CANCELLATION_REASON_TECHNICAL_ISSUES,
  },
  { value: "notMeetNeeds", label: CANCELLATION_REASON_NOT_MEET_NEEDS },
  {
    value: "difficultToUse",
    label: CANCELLATION_REASON_DIFFICULT_TO_USE,
  },
  {
    value: "situationChanged",
    label: CANCELLATION_REASON_SITUATION_CHANGED,
  },
  { value: "temporaryNeed", label: CANCELLATION_REASON_TEMPORARY_NEED },
  {
    value: "securityConcerns",
    label: CANCELLATION_REASON_SECURITY_CONCERNS,
  },
  { value: "other", label: CANCELLATION_REASON_OTHER },
];

const ModalCancelSubscription = ({
  modalOpen,
  onCloseModal,
  onConfirmCancellation,
  loading = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatchApp();
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const subscriptionPlan = useSelectorApp(
    (state) => state.payments.subscriptionPlan
  );
  const subscriptionPlanInfo = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo
  );
  const typeOfPlans = useSelectorApp((state) => state.payments.typeOfPlans);
  const creditPricingVersion = useSelectorApp(
    (state) => state.payments.creditPricingVersion
  );
  const plans = useSelectorApp((state) => state.payments.plans);

  const entryPlan = getEntryPlanForVersion(creditPricingVersion);
  const entryPlanCatalog = entryPlan ? plans?.[entryPlan] : null;
  const entryMonthlyPrice = entryPlanCatalog?.monthly?.credits?.price;
  const entryPlanCredits = entryPlanCatalog?.benefits?.credits;

  const showRetentionDowngradeOffer =
    Boolean(subscriptionPlan) &&
    typeOfPlans === PLAN_TYPE_CREDITS &&
    Boolean(entryPlan) &&
    subscriptionPlan !== entryPlan &&
    typeof entryMonthlyPrice === "number" &&
    typeof entryPlanCredits === "number";

  const [destructiveConfirmOpen, setDestructiveConfirmOpen] = useState(false);
  const [isTakingRetentionOffer, setIsTakingRetentionOffer] = useState(false);
  const [pendingCancellation, setPendingCancellation] = useState(null);
  const minChars = 100;
  const maxChars = 500;

  const confirmWord = t(MODAL_CANCEL_SUBSCRIPTION_CONFIRM_WORD);

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      selectedReasonValue: "",
      reason: "",
      destructiveConfirm: "",
    },
    mode: "onChange",
  });

  const reasonValue = watch("reason") || "";
  const selectedReasonValue = watch("selectedReasonValue") || "";
  const destructiveConfirmText = watch("destructiveConfirm") || "";
  const charCount = reasonValue.length;

  const isTextFieldValid = charCount >= minChars;
  const isSelectValid = selectedReasonValue.length > 0;
  const isFormValid = isTextFieldValid && isSelectValid;
  const isDestructiveConfirmValid =
    destructiveConfirmText.trim() === confirmWord;

  useEffect(() => {
    if (!modalOpen) {
      reset();
      setDestructiveConfirmOpen(false);
      setPendingCancellation(null);
    }
  }, [modalOpen, reset]);

  const onCloseDestructiveConfirm = () => {
    if (loading) return;
    setDestructiveConfirmOpen(false);
    setPendingCancellation(null);
    reset((formValues) => ({
      ...formValues,
      destructiveConfirm: "",
    }));
  };

  const onConfirmDestructiveCancellation = () => {
    if (
      !pendingCancellation ||
      !onConfirmCancellation ||
      !isDestructiveConfirmValid
    ) {
      return;
    }
    onConfirmCancellation(pendingCancellation);
    setDestructiveConfirmOpen(false);
    setPendingCancellation(null);
  };

  const onSubmit = (data) => {
    if (!isFormValid || !onConfirmCancellation) return;

    const cancellationPayload = {
      reason: data.reason,
      selectedReason: data.selectedReasonValue,
    };

    if (!subscriptionPlan) {
      onConfirmCancellation(cancellationPayload);
      return;
    }

    setPendingCancellation(cancellationPayload);
    setDestructiveConfirmOpen(true);
  };

  const handleConfirm = () => {
    handleSubmit(onSubmit)();
  };

  const onTakeRetentionOffer = async () => {
    setIsTakingRetentionOffer(true);
    const result = await dispatch(startDowngradeSubscriptionForRetention());
    setIsTakingRetentionOffer(false);
    if (result?.ok) {
      SimpleAlert({
        title: t(MODAL_CANCEL_SUBSCRIPTION_RETENTION_OFFER_REDEEMED_SUCCESS),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      onCloseModal();
    }
  };

  return (
    <>
      <ModalBasicLayout
        modalOpen={modalOpen && !destructiveConfirmOpen}
        onCloseModal={onCloseModal}
        title={t(MODAL_CANCEL_SUBSCRIPTION_TITLE)}
        description=""
        maxWidth="xs"
        fullWidth
        showCloseButton={true}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ lineHeight: 1.5, mb: 1.5 }}>
            {t(MODAL_CANCEL_SUBSCRIPTION_SUBTITLE)}
          </Typography>
          <Typography variant="body1" component="div" sx={{ lineHeight: 1.5 }}>
            <Trans
              i18nKey={MODAL_CANCEL_SUBSCRIPTION_SUBTITLE_WARNING}
              components={cancelFlowTransComponents}
            />
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 600 }}>
            <CancellationReasonForm
              control={control}
              errors={errors}
              charCount={charCount}
              minChars={minChars}
              maxChars={maxChars}
              cancellationReasons={cancellationReasons}
            />
          </Box>
        </Box>

        {showRetentionDowngradeOffer ? (
          <CancelSubscriptionBasicRetentionCallout
            renewDate={subscriptionPlanInfo?.renewDate}
            monthlyPrice={entryMonthlyPrice}
            credits={entryPlanCredits}
            onTakeOffer={onTakeRetentionOffer}
            loading={isTakingRetentionOffer}
          />
        ) : null}

        <DialogActions
          sx={{
            p: 0,
          }}
        >
          <ContainerModalActions
            hasPadding={true}
            propsPrimaryButton={{
              showButton: true,
              text: BUTTON_GO_BACK,
              onClick: onCloseModal,
            }}
            propsSecondaryButton={{
              showButton: true,
              disabled: !isFormValid || loading,
              text: MODAL_CANCEL_SUBSCRIPTION_CONFIRM_BUTTON,
              onClick: async () => {
                handleConfirm();
              },
            }}
          />
        </DialogActions>
      </ModalBasicLayout>

      <ModalBasicLayout
        modalOpen={Boolean(modalOpen && destructiveConfirmOpen)}
        onCloseModal={onCloseDestructiveConfirm}
        title={t(MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_TITLE)}
        description=""
        maxWidth="xs"
        fullWidth
        showCloseButton={!loading}
      >
        <CancelSubscriptionDestructiveContent
          NAME_BUSINESS={NAME_BUSINESS}
          control={control}
          errors={errors}
        />

        <DialogActions sx={{ p: 0 }}>
          <ContainerModalActions
            hasPadding={true}
            sx={{
              flexDirection: "column",
              gap: 1,
            }}
            propsPrimaryButton={{
              showButton: true,
              disabled: loading,
              text: BUTTON_KEEP_MY_ACCOUNT,
              onClick: onCloseDestructiveConfirm,
            }}
            propsSecondaryButton={{
              showButton: true,
              disabled: !isDestructiveConfirmValid || loading,
              loading,
              text: BUTTON_CANCEL_PLAN_AND_DELETE_CAMPAIGNS,
              onClick: onConfirmDestructiveCancellation,
            }}
          />
        </DialogActions>
      </ModalBasicLayout>
    </>
  );
};

export default ModalCancelSubscription;
